import React, { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileUploadIcon from "@mui/icons-material/FileUpload";

interface FenBarProps {
  fen: string;
  onCopyFen: () => void;
  onLoadCustomFen: (inputFen: string) => { success: boolean; error?: string };
}

export const FenBar: React.FC<FenBarProps> = ({
  fen,
  onCopyFen,
  onLoadCustomFen,
}) => {
  const [fenDialogOpen, setFenDialogOpen] = useState<boolean>(false);
  const [inputFen, setInputFen] = useState<string>("");
  const [fenDialogError, setFenDialogError] = useState<string | null>(null);

  const handleOpenDialog = () => {
    setInputFen(fen);
    setFenDialogError(null);
    setFenDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setFenDialogOpen(false);
    setFenDialogError(null);
  };

  const handleApplyFen = () => {
    const result = onLoadCustomFen(inputFen.trim());
    if (result.success) {
      setFenDialogOpen(false);
      setInputFen("");
    } else {
      setFenDialogError(result.error || "Invalid FEN string");
    }
  };

  return (
    <>
      <Box
        sx={{
          mt: 2.5,
          p: 1.5,
          borderRadius: 2,
          bgcolor: "rgba(0, 0, 0, 0.4)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Box sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            <Typography variant="caption" color="grey.400" sx={{ display: "block", fontSize: "0.7rem" }}>
              FEN POSITION:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.8rem",
                color: "#e2e8f0",
                wordBreak: "break-all",
                userSelect: "all",
              }}
            >
              {fen}
            </Typography>
          </Box>

          <Stack direction="row" spacing={0.5} flexShrink={0}>
            <Tooltip title="Copy FEN">
              <IconButton size="small" onClick={onCopyFen} sx={{ color: "grey.300" }}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Load Custom FEN">
              <IconButton size="small" onClick={handleOpenDialog} sx={{ color: "grey.300" }}>
                <FileUploadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      {/* Load Custom FEN Dialog */}
      <Dialog
        open={fenDialogOpen}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: "#1e293b",
            color: "white",
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>Load FEN Position</DialogTitle>
        <DialogContent>
          {fenDialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {fenDialogError}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={2}
            label="FEN String"
            variant="outlined"
            value={inputFen}
            onChange={(e) => setInputFen(e.target.value)}
            sx={{
              mt: 1,
              "& .MuiOutlinedInput-root": {
                fontFamily: "monospace",
                color: "white",
                "& fieldset": { borderColor: "rgba(255,255,255,0.3)" },
                "&:hover fieldset": { borderColor: "primary.main" },
              },
              "& .MuiInputLabel-root": { color: "grey.400" },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog} sx={{ color: "grey.400" }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleApplyFen}>
            Load Position
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FenBar;
